// ============================================================================
// EditSubSlideOver — full subcontractor entity edit + the merged
// "Documents & Compliance" section (partner_documents canonical, Task 1
// 2026-07-10). Extracted from subcontractors-page.tsx (2026-07-10 REV 2) so it
// can be opened as the SECONDARY edit action from the routed detail page as
// well as anywhere else that needs it.
//
// Self-contained: loads the full service_providers row + assignments on open,
// writes provider fields to service_providers (RLS: all full employees) and
// notes to partner_onboarding (by onboarding_id). W9/COI/contract are edited in
// the PartnerDocumentsSection (self-saving, canonical source).
// ============================================================================
import { useEffect, useState } from 'react';
import { UserX, X } from 'lucide-react';
import { Button, Skeleton, useToast } from '../../shared/components';
import { supabase } from '../../shared/supabase';
import { overlayClickClose } from '../../shared/overlay-close';
import PartnerDocumentsSection from './partner-documents-section';
import { errorMessage } from '../../shared/errors';
import { marketOptions, useMarkets } from '../../shared/markets';
import { SUB_STATUSES, subStatusLabel } from '../subcontractors/stages';

// Two values, because two is what the question has: this payee is a business we
// subcontract to, or a person we pay directly. They are the two entries of the
// one classification list that can describe a provider; the other two are
// payroll classes and belong to the worker, not to the company.
//
// ⛔ The list this replaced had six free-text values and no constraint behind
// it. Two of them — a supply vendor and professional services — contradicted the
// class already set on the same record, and two more were different spellings of
// the same thing, split by the date a function started writing one of them onto
// everything it created. Do not add a value here: a seventh spelling is how the
// first six happened.
const WORKER_CLASSES: { value: string; label: string }[] = [
  { value: 'subcontractor', label: 'Subcontractor — a business' },
  { value: 'individual_1099', label: '1099 individual — a person, paid directly' },
];
// The status words are the CHECK on provider_status, declared once (item #683).
const PROVIDER_STATUSES = SUB_STATUSES;

interface ProviderEditFields {
  partner_name: string;
  legal_business_name: string;
  owner_name: string;
  worker_class: string;
  provider_status: string;
  address: string;
  email: string;
  phone: string;
  primary_poc_name: string;
  primary_poc_phone: string;
  primary_poc_email: string;
  secondary_poc_name: string;
  secondary_poc_phone: string;
  secondary_poc_email: string;
}
type ProviderAssignment = { assignment_id: string; facility_name: string; status: string };

export interface EditSubSlideOverProps {
  providerId: string;
  /** partner_onboarding id — null when the sub has no onboarding record yet
      (W9/COI/notes stay locked; entity fields still save). */
  onboardingId: string | null;
  /** Display name for the header (go-by). */
  displayName: string | null;
  /** Current status — drives the footer Deactivate visibility. */
  providerStatus?: string | null;
  /** Initial notes (partner_onboarding.notes). */
  initialNotes?: string | null;
  onClose: () => void;
  /** Called after a successful save so the caller can refetch. */
  onSaved?: () => void;
  /** AFB-0028 — opens the dependency-guarded deactivate modal (hr/superuser). */
  onDeactivate?: () => void;
}

export default function EditSubSlideOver({
  providerId,
  onboardingId,
  displayName,
  providerStatus,
  initialNotes,
  onClose,
  onSaved,
  onDeactivate,
}: EditSubSlideOverProps) {
  const toast = useToast();
  const [notes, setNotes] = useState(initialNotes ?? '');
  const [saving, setSaving] = useState(false);
  const [prov, setProv] = useState<ProviderEditFields | null>(null);
  const [markets, setMarkets] = useState<string[]>([]);
  const { markets: marketList } = useMarkets();
  const [assignments, setAssignments] = useState<ProviderAssignment[]>([]);
  const [loadingProv, setLoadingProv] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingProv(true);
      const { data, error: loadErr } = await supabase
        .from('service_providers')
        .select(
          'partner_name, legal_business_name, owner_name, worker_class, provider_status, coverage_markets, address, email, phone, primary_poc_name, primary_poc_phone, primary_poc_email, secondary_poc_name, secondary_poc_phone, secondary_poc_email',
        )
        .eq('provider_id', providerId)
        .maybeSingle();
      const asg = await supabase
        .from('service_assignments')
        .select('assignment_id, assignment_status, facility_id')
        .eq('provider_id', providerId);
      const facIds = [
        ...new Set(
          ((asg.data ?? []) as { facility_id: string | null }[])
            .map((a) => a.facility_id)
            .filter(Boolean) as string[],
        ),
      ];
      const facRes = facIds.length
        ? await supabase.from('facilities').select('facility_id, facility_name').in('facility_id', facIds)
        : { data: [] };
      const facName: Record<string, string> = {};
      for (const f of (facRes.data ?? []) as { facility_id: string; facility_name: string | null }[]) {
        facName[f.facility_id] = f.facility_name ?? f.facility_id;
      }
      if (cancelled) return;
      // ⛔ A failed read must never become an empty form whose Save writes blanks over a
      // real sub (PLAYBOOK §5b). Say so and keep the form closed.
      if (loadErr || !data) {
        toast.error('Could not load this sub for editing', { description: loadErr?.message ?? 'No record came back.' });
        setLoadingProv(false);
        return;
      }
      setMarkets(((data as { coverage_markets?: string[] | null }).coverage_markets) ?? []);
      const d = (data ?? {}) as Partial<Record<keyof ProviderEditFields, string | null>>;
      setProv({
        partner_name: d.partner_name ?? '',
        legal_business_name: d.legal_business_name ?? '',
        owner_name: d.owner_name ?? '',
        worker_class: d.worker_class ?? '',
        provider_status: d.provider_status ?? '',
        address: d.address ?? '',
        email: d.email ?? '',
        phone: d.phone ?? '',
        primary_poc_name: d.primary_poc_name ?? '',
        primary_poc_phone: d.primary_poc_phone ?? '',
        primary_poc_email: d.primary_poc_email ?? '',
        secondary_poc_name: d.secondary_poc_name ?? '',
        secondary_poc_phone: d.secondary_poc_phone ?? '',
        secondary_poc_email: d.secondary_poc_email ?? '',
      });
      setAssignments(
        ((asg.data ?? []) as { assignment_id: string; assignment_status: string | null; facility_id: string | null }[]).map(
          (a) => ({
            assignment_id: a.assignment_id,
            status: a.assignment_status ?? '—',
            facility_name: a.facility_id ? facName[a.facility_id] ?? a.facility_id : '—',
          }),
        ),
      );
      setLoadingProv(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [providerId]);

  function setP<K extends keyof ProviderEditFields>(k: K, v: string) {
    setProv((p) => (p ? { ...p, [k]: v } : p));
  }

  async function save() {
    if (!prov) return;
    if (prov.partner_name.trim().length === 0) {
      toast.error('Name (go-by) is required');
      return;
    }
    setSaving(true);
    try {
      const provPatch = {
        partner_name: prov.partner_name.trim(),
        legal_business_name: prov.legal_business_name.trim() || null,
        owner_name: prov.owner_name.trim() || null,
        worker_class: prov.worker_class || null,
        provider_status: prov.provider_status || null,
        coverage_markets: markets.length ? markets : null,
        address: prov.address.trim() || null,
        email: prov.email.trim() || null,
        phone: prov.phone.trim() || null,
        primary_poc_name: prov.primary_poc_name.trim() || null,
        primary_poc_phone: prov.primary_poc_phone.trim() || null,
        primary_poc_email: prov.primary_poc_email.trim() || null,
        secondary_poc_name: prov.secondary_poc_name.trim() || null,
        secondary_poc_phone: prov.secondary_poc_phone.trim() || null,
        secondary_poc_email: prov.secondary_poc_email.trim() || null,
      };
      const { error: provErr } = await supabase
        .from('service_providers')
        .update(provPatch)
        .eq('provider_id', providerId);
      if (provErr) throw provErr;

      // Notes live on partner_onboarding; W9/COI/contract writes are handled by
      // the Documents & Compliance section (self-saving, canonical source).
      if (onboardingId && (notes || '') !== (initialNotes ?? '')) {
        const { error: noteErr } = await supabase
          .from('partner_onboarding')
          .update({ notes: notes || null })
          .eq('onboarding_id', onboardingId);
        if (noteErr) throw noteErr;
      }
      toast.success('Subcontractor updated');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error('Could not save', { description: errorMessage(e, '') });
    } finally {
      setSaving(false);
    }
  }

  const inputCls = 'w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm';

  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-tcs-scrim/60" role="dialog" aria-modal="true" {...overlayClickClose(onClose)}>
      <div className="flex h-full w-full max-w-md flex-col overflow-hidden bg-tcs-raised shadow-xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-tcs-border px-5 py-4">
          <div>
            <div className="text-sm font-semibold text-tcs-text">{displayName ?? providerId}</div>
            <div className="text-[11px] text-tcs-text-muted">Edit subcontractor</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-tcs-surface">
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="space-y-4 overflow-y-auto p-5">
          {loadingProv || !prov ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-9" />
              ))}
            </div>
          ) : (
            <>
              <SectionLabel>Identity</SectionLabel>
              <Field label="Name (go-by) *">
                <input value={prov.partner_name} onChange={(e) => setP('partner_name', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Legal business name (on contract)">
                <input value={prov.legal_business_name} onChange={(e) => setP('legal_business_name', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Owner">
                <input value={prov.owner_name} onChange={(e) => setP('owner_name', e.target.value)} className={inputCls} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Paid as">
                  <select value={prov.worker_class} onChange={(e) => setP('worker_class', e.target.value)} className={inputCls}>
                    <option value="">—</option>
                    {WORKER_CLASSES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Status">
                  <select value={prov.provider_status} onChange={(e) => setP('provider_status', e.target.value)} className={inputCls}>
                    <option value="">—</option>
                    {PROVIDER_STATUSES.map((s) => (
                      <option key={s} value={s}>{subStatusLabel(s)}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <SectionLabel>Location &amp; contact</SectionLabel>
              <Field label="Markets they cover">
                <div className="flex flex-wrap gap-1.5">
                  {marketOptions(marketList).map((m) => (
                    <button
                      key={m}
                      type="button"
                      aria-pressed={markets.includes(m)}
                      onClick={() => setMarkets((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]))}
                      className={`rounded-full border px-2.5 py-1 text-xs ${markets.includes(m) ? 'border-tcs-text bg-tcs-inverse text-tcs-inverse-ink' : 'border-tcs-border text-tcs-text'}`}
                    >
                      {m.replace(/\s*Metro$/, '')}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Address">
                <input value={prov.address} onChange={(e) => setP('address', e.target.value)} className={inputCls} placeholder="Street, city, state, ZIP" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email"><input value={prov.email} onChange={(e) => setP('email', e.target.value)} className={inputCls} /></Field>
                <Field label="Phone"><input value={prov.phone} onChange={(e) => setP('phone', e.target.value)} className={inputCls} /></Field>
              </div>

              <SectionLabel>Primary contact (POC)</SectionLabel>
              <Field label="Name"><input value={prov.primary_poc_name} onChange={(e) => setP('primary_poc_name', e.target.value)} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone"><input value={prov.primary_poc_phone} onChange={(e) => setP('primary_poc_phone', e.target.value)} className={inputCls} /></Field>
                <Field label="Email"><input value={prov.primary_poc_email} onChange={(e) => setP('primary_poc_email', e.target.value)} className={inputCls} /></Field>
              </div>

              <SectionLabel>Secondary contact (POC)</SectionLabel>
              <Field label="Name"><input value={prov.secondary_poc_name} onChange={(e) => setP('secondary_poc_name', e.target.value)} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone"><input value={prov.secondary_poc_phone} onChange={(e) => setP('secondary_poc_phone', e.target.value)} className={inputCls} /></Field>
                <Field label="Email"><input value={prov.secondary_poc_email} onChange={(e) => setP('secondary_poc_email', e.target.value)} className={inputCls} /></Field>
              </div>

              <SectionLabel>Assignments ({assignments.length})</SectionLabel>
              {assignments.length === 0 ? (
                <p className="text-xs text-tcs-text-muted">No service assignments on file.</p>
              ) : (
                <ul className="space-y-1 text-xs">
                  {assignments.map((a) => (
                    <li key={a.assignment_id} className="flex items-center justify-between rounded border border-tcs-border px-2 py-1">
                      <span className="text-tcs-text">{a.facility_name}</span>
                      <span className="text-tcs-text-muted">{a.status}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-tcs-text-muted">
                Assignments are managed from the facility card (add/swap/end a sub there). Shown here for reference.
              </p>

              {/* Documents & Compliance — single surface bound to
                  partner_documents (canonical, Task 1 2026-07-10). */}
              {!onboardingId ? (
                <>
                  <SectionLabel>Documents &amp; Compliance</SectionLabel>
                  <p className="rounded bg-st-warn-bg px-2 py-1 text-[11px] text-st-warn-fg">
                    No setup record yet — W-9 / COI / documents unlock once this sub&rsquo;s setup is complete. Entity fields above still save.
                  </p>
                </>
              ) : (
                <PartnerDocumentsSection
                  providerId={providerId}
                  onboardingId={onboardingId}
                  title="Documents & Compliance"
                />
              )}
              <SectionLabel>Notes</SectionLabel>
              <Field label="Notes">
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={`${inputCls} resize-y`} disabled={!onboardingId} />
              </Field>
            </>
          )}
        </div>
        <footer className="flex items-center justify-between gap-2 border-t border-tcs-border px-5 py-3">
          <div>
            {onDeactivate && providerStatus !== 'Inactive' && (
              <Button size="sm" variant="ghost" icon={<UserX className="h-3.5 w-3.5" />} onClick={onDeactivate}>
                Deactivate
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button size="sm" loading={saving} disabled={loadingProv} onClick={save}>Save</Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-tcs-text-secondary">{label}</label>
      {children}
    </div>
  );
}
